import { S, ui } from "../../app/store.js";
import { V } from "../../app/pages.js";
import { can, me } from "../../services/access.js";
import { esc } from "../../domain/format.js";
import { request, loadWorkspace } from "../../services/api.js";
import {
  admCompany,
  admSecurity,
  admData,
  admUsers,
  admRoles,
} from "../admin/page.js";
import { accountActivationForm } from "../admin/workspace-tools.js";
import { auditTable } from "../audit/page.js";
import { chip } from "../../components/ui.js";
import {
  accountSetupTutorial,
  installAuthenticatorChoices,
} from "../../components/account-setup-guide.js";

let installed = false;
let refreshing;
const repaint = () =>
  globalThis.dispatchEvent(new CustomEvent("milltech:render"));
const TABS = [
  ["account", "Account"],
  ["security", "Security"],
  ["workspace", "Workspace"],
  ["policy", "Sign-In Policy"],
  ["data", "Data & Privacy"],
  ["users", "Users"],
  ["roles", "Roles & Permissions"],
  ["audit", "Audit Trail"],
];
const tabAllowed = (key) =>
  personalTabs.has(key) ||
  (key === "users" || key === "roles"
    ? can("admin.users")
    : key === "audit"
      ? can("audit.view")
      : can("sys.admin"));
const personalTabs = new Set(["account", "security"]);
const ownInfo = () => {
  const user = me();
  const account = ui.accountInfo;
  return account?.email?.toLowerCase() === user.email?.toLowerCase()
    ? account
    : {};
};
const dateTime = (value) => {
  const date = new Date(value || "");
  return Number.isNaN(date.getTime())
    ? "—"
    : date.toLocaleString("en-PH", {
        timeZone: "Asia/Manila",
        dateStyle: "medium",
        timeStyle: "short",
      });
};
function status() {
  return `<p class="settings-status${ui.settingsError ? " is-error" : ""}" role="status">${esc(ui.settingsMessage || "")}</p>`;
}
function accountPage() {
  const user = me(),
    info = ownInfo();
  const mode = info.mode || ui.workspaceMode || "demo";
  return `<section class="panel settings-panel"><div class="panel-h"><div><h2>Your Profile</h2><span class="muted">Manage the name shown on your account.</span></div></div><form id="account-profile" class="settings-profile-form"><label>Display Name<input name="name" autocomplete="name" maxlength="120" value="${esc(ui.settingsNameDraft ?? info.name ?? user.name)}" required></label><label>Work Email<input type="email" autocomplete="username" value="${esc(info.email || user.email || "")}" data-case="preserve" readonly aria-readonly="true"></label><dl class="settings-details"><div><dt>Role</dt><dd>${esc(S.roles[info.role || user.role]?.name || info.role || user.role)}</dd></div><div><dt>Workspace</dt><dd>${mode === "live" ? "Live · Company Records" : "Demo · Sample Data"}</dd></div></dl><p class="small muted settings-span">Your work email identifies your sign-in account. Ask an administrator if it needs to change.</p><div class="settings-span"><button class="pri" type="submit"${ui.settingsBusy ? " disabled" : ""}>${ui.settingsBusy ? "Saving…" : "Save Profile"}</button></div></form></section>`;
}
function authenticatorAppForm(value) {
  const current = ["google", "microsoft"].includes(value) ? value : "";
  return `<form id="authenticator-app-form" class="settings-authenticator-app"><label>App used for Milltech codes<select name="app" required><option value="" disabled${current ? "" : " selected"}>Choose app</option><option value="google"${current === "google" ? " selected" : ""}>Google Authenticator</option><option value="microsoft"${current === "microsoft" ? " selected" : ""}>Microsoft Authenticator</option></select></label><button type="submit" class="out"${ui.settingsBusy ? " disabled" : ""}>Save App</button><p class="small muted">Select the app currently giving you Milltech codes. This is a user-reported label; the codes cannot identify the app.</p></form>`;
}
function securityPage() {
  const user = me(),
    info = ownInfo();
  const enabled = info.mfaEnabled ?? !!user.mfa;
  const required = info.mfaRequired ?? !!S.settings.mfaRequired;
  const minimum = Math.max(12, info.passwordMinimum || S.settings.pwMin || 12);
  return `<div class="settings-security"><section class="panel settings-panel"><div class="panel-h"><div><h2>Password</h2><span class="muted">Choose a password that you use only for Milltech.</span></div></div><p class="small muted">Your password must have at least ${minimum} characters. Changing it ends your other sessions and asks you to sign in again.</p><button type="button" data-act="change-password">Change Password</button></section><section class="panel settings-panel"><div class="panel-h"><div><h2>Authenticator</h2><span class="muted">A second verification step protects your account.</span></div>${chip(enabled ? "Enabled" : "Not Set Up", enabled ? "good" : required ? "warn" : "mut")}</div><p class="small muted">${enabled ? "Your current authenticator stays active until a new authenticator is verified." : required ? "Set up your authenticator to meet the workspace’s sign-in policy." : "Use Google Authenticator or Microsoft Authenticator for an additional sign-in check."}</p>${enabled ? authenticatorAppForm(info.authenticatorApp ?? user.authenticatorApp) : ""}${accountSetupTutorial()}<button type="button" data-act="my-mfa">${enabled ? "Connect A New Authenticator" : "Set Up Authenticator"}</button></section><section class="panel settings-panel"><div class="panel-h"><div><h2>Devices & Sessions</h2><span class="muted">Sign out other devices while keeping this session open.</span></div></div><dl class="settings-details"><div><dt>Current Session Started</dt><dd>${esc(dateTime(info.session?.createdAt))}</dd></div><div><dt>Session Expires</dt><dd>${esc(dateTime(info.session?.expiresAt))}</dd></div></dl><button type="button" data-settings-action="revoke-sessions"${ui.settingsBusy ? " disabled" : ""}>Sign Out Other Devices</button></section></div>`;
}
V.settings = () => {
  const tabs = TABS.filter(([key]) => tabAllowed(key));
  const tab = tabs.some(([key]) => key === ui.settingsTab)
    ? ui.settingsTab
    : "account";
  const content = {
    account: accountPage,
    security: securityPage,
    workspace: admCompany,
    policy: admSecurity,
    data: admData,
    users: () => admUsers() + accountActivationForm(),
    roles: admRoles,
    audit: auditTable,
  }[tab]();
  return `<div class="settings-page"><div class="settings-intro"><p class="muted">Manage your account and the settings available to your role.</p><button type="button" class="out" data-settings-action="refresh">Refresh Account</button></div><div class="settings-tabs" role="tablist" aria-label="Settings Sections">${tabs.map(([key, label]) => `<button type="button" role="tab" id="settings-tab-${key}" aria-selected="${tab === key}" aria-controls="settings-content" tabindex="${tab === key ? 0 : -1}" data-settings-tab="${key}">${label}</button>`).join("")}</div>${status()}<div id="settings-content" role="tabpanel" aria-labelledby="settings-tab-${tab}">${content}</div></div>`;
};

export async function refreshAccountInfo({ render = true } = {}) {
  if (!ui.user) return null;
  const user = ui.user;
  if (refreshing?.user === user) return refreshing.promise;
  const promise = (async () => {
    try {
      const info = await request("/api/account");
      if (ui.user !== user) return null;
      ui.accountInfo = info;
      ui.securityUser = { name: info.name, email: info.email };
      ui.passwordMinimum = info.passwordMinimum;
      if (ui.settingsError) {
        ui.settingsError = false;
        ui.settingsMessage = "";
      }
      return info;
    } catch (error) {
      if (ui.user === user) {
        ui.settingsError = true;
        ui.settingsMessage = error.message;
      }
      return null;
    } finally {
      if (refreshing?.user === user) refreshing = null;
      if (render && ui.user === user && ui.view === "settings") repaint();
    }
  })();
  refreshing = { user, promise };
  return promise;
}
function setBusy(value) {
  ui.settingsBusy = value;
  for (const button of document.querySelectorAll(".settings-page button"))
    button.disabled = value;
}
async function perform(action) {
  if (ui.settingsBusy) return;
  ui.settingsError = false;
  ui.settingsMessage = "";
  setBusy(true);
  try {
    await action();
  } catch (error) {
    ui.settingsError = true;
    ui.settingsMessage = error.message;
  } finally {
    setBusy(false);
    if (ui.user && ui.view === "settings") repaint();
  }
}
export function installSettingsEvents(root = document) {
  installAuthenticatorChoices(root);
  if (installed) return;
  installed = true;
  root.addEventListener("submit", (event) => {
    if (event.target.id === "authenticator-app-form") {
      event.preventDefault();
      const app = new FormData(event.target).get("app");
      void perform(async () => {
        await request("/api/account/authenticator-app", {
          method: "POST",
          body: JSON.stringify({ app }),
        });
        await loadWorkspace();
        await refreshAccountInfo({ render: false });
        ui.settingsMessage = "Authenticator app saved.";
      });
      return;
    }
    if (event.target.id !== "account-profile") return;
    event.preventDefault();
    const name = String(new FormData(event.target).get("name") || "").trim();
    ui.settingsNameDraft = name;
    if (!name) {
      ui.settingsError = true;
      ui.settingsMessage = "Enter your display name.";
      repaint();
      return;
    }
    void perform(async () => {
      await request("/api/account/profile", {
        method: "POST",
        body: JSON.stringify({ name }),
      });
      await loadWorkspace();
      const info = await refreshAccountInfo({ render: false });
      ui.settingsNameDraft = undefined;
      ui.settingsMessage = info
        ? "Your profile was updated."
        : "Your profile was updated. Select Refresh Account to reload your account details.";
    });
  });
  root.addEventListener("click", (event) => {
    const button = event.target.closest?.("button");
    if (!button) return;
    if (button.dataset.go === "settings") {
      queueMicrotask(() => {
        void refreshAccountInfo();
      });
      return;
    }
    if (button.dataset.settingsTab) {
      const tab = button.dataset.settingsTab;
      if (!tabAllowed(tab)) return;
      if (!TABS.some(([key]) => key === tab)) return;
      ui.settingsTab = tab;
      ui.settingsMessage = "";
      ui.settingsError = false;
      repaint();
      document.getElementById(`settings-tab-${tab}`)?.focus();
      return;
    }
    if (button.dataset.accountsTab) {
      if (!can("admin.users")) return;
      if (!["users", "roles"].includes(button.dataset.accountsTab)) return;
      ui.accountsTab = button.dataset.accountsTab;
      repaint();
      document.getElementById(`accounts-tab-${ui.accountsTab}`)?.focus();
      return;
    }
    if (button.dataset.settingsAction === "refresh") {
      void refreshAccountInfo();
      return;
    }
    if (button.dataset.settingsAction === "revoke-sessions")
      void perform(async () => {
        await request("/api/auth/revoke-other-sessions", {
          method: "POST",
          body: "{}",
        });
        ui.settingsMessage =
          "Other devices were signed out. This session remains open.";
      });
  });
  root.addEventListener("keydown", (event) => {
    const button = event.target.closest?.(
      "[data-settings-tab], [data-accounts-tab]",
    );
    if (
      !button ||
      !["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)
    )
      return;
    const tabs = [
      ...button.closest('[role="tablist"]').querySelectorAll('[role="tab"]'),
    ];
    const index = tabs.indexOf(button);
    const next =
      event.key === "Home"
        ? 0
        : event.key === "End"
          ? tabs.length - 1
          : (index + (event.key === "ArrowRight" ? 1 : -1) + tabs.length) %
            tabs.length;
    event.preventDefault();
    tabs[next].click();
  });
}
